// TestAI.tsx - Version finale sans TypingMessage
import { FC, useContext, useEffect, useState, useRef, ReactElement, useCallback, useMemo } from "react";
import "./modern-chat.scss";
import { chatCompletionsPremium, file_upload, get_chat_history, saveConversation, isToolRoutingMessage } from "features/ai-assistant";
import PageHeader from "components/PageHeader";
import { Message, UserContext } from "features/account";
import useWebSocket from "react-use-websocket";
import React from "react";
import apiInstance from "shared/api/axiosConfig";
import UnauthorizedPage from "components/UnauthorizedPage";
import { getSubscription } from "features/account";
import Loader from "shared/ui/Loader";
import MessageBubble from "features/ai-assistant/components/AI_Chat/MessageBubble";
import ChatInput from "features/ai-assistant/components/AI_Chat/ChatInput";
import ToolSelectDialog, { TOOL_LABELS, TOOL_PREFILL } from "features/ai-assistant/components/AI_Chat/ToolSelectDialog";
import SuggestedQuestions from "features/ai-assistant/components/AI_Chat/SuggestedQuestions";
import { motion, AnimatePresence } from "framer-motion";
import imgKuma from "assets/img/img_Kuma.png";

import { ReactComponent as IconBack } from "assets/img/icons/icon_arrow-Left.svg";
import { ReactComponent as IconTool } from "assets/img/icons/settings.svg";
import { useNavigate } from "react-router-dom";
import { generateId } from "shared/lib/generateId";

// Segment de route par outil. Les routes du front sont préfixées par audience
// (/particuliers ou /professionnels, cf. Home.tsx) : le préfixe ne peut donc pas être figé
// ici, il est ajouté à l'exécution selon le type de compte (voir toolLinksResolved).
const TOOL_LINKS: { [key: string]: string } = {
  ID_Tracker: "pieces-identite",
  Filigrane: "filter",
  ID_Locker: "id-locker",
  ID_Verify: "verifier-document",
  // Kuma n'émet plus #ID_Assistant# (il passe par la création de demande auprès d'un
  // conseiller), mais les conversations déjà persistées peuvent encore en contenir.
  ID_Assistant: "id-assistant",
};

// Questions d'accueil — une par profil d'intention (cf. aiguillage du prompt Kuma) :
// A « je m'informe », B « je découvre ID Protect », C « je veux agir / être aidé »
const SUGGESTED_QUESTIONS = [
  {
    id: "inform_scam",
    text: "On me demande mes papiers, est-ce une arnaque ?",
  },
  {
    id: "discover_idprotect",
    text: "Que propose ID Protect ?",
  },
  {
    id: "victim_help",
    text: "J'ai été victime d'une fraude, aidez-moi",
  },
];

const TestAI = () => {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isLoadingOffre, setIsLoadingOffre] = useState(true);
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  // Réponses rapides générées par le serveur, adaptées à la dernière réponse de Kuma (vide = suggestions par défaut)
  const [quickReplies, setQuickReplies] = useState<string[]>([]);
  // Les suggestions sont masquées pendant le chargement + l'animation de frappe, et n'apparaissent qu'à la fin
  const [suggestionsReady, setSuggestionsReady] = useState(true);
  const pendingQuickReplies = useRef<string[]>([]);
  const [showToolDialog, setShowToolDialog] = useState(false);
  const { user, getMe, ai_thread, ai_conversation, setAi_conversation, userType } = useContext(UserContext);

  // Les jetons émis par Kuma (#Filigrane#…) ne portent que le segment de route : sans le
  // préfixe d'audience, le lien ne correspond à aucune route et le clic tombe dans le vide.
  // Le type de compte n'est pas normalisé côté front (le localStorage porte "partner" ou
  // "individual", certains écrans posent "professionnels"/"particuliers") : on tolère les deux.
  const toolLinksResolved = useMemo(() => {
    const isPartner = userType === "partner" || userType === "professionnels";
    const base = isPartner ? "/professionnels" : "/particuliers";
    return Object.keys(TOOL_LINKS).reduce<{ [key: string]: string }>((acc, key) => {
      acc[key] = `${base}/${TOOL_LINKS[key]}`;
      return acc;
    }, {});
  }, [userType]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesAreaRef = useRef<HTMLDivElement>(null);

  const baseURL = apiInstance.getUri();
  const wsUrl = user?.clientNumber ? `${baseURL.replace(/^http/, "ws")}/ws/` : null;
  const [offer, setOffer] = useState<any>();
  const type = user?.type;
  const navigate = useNavigate();

  // Fonction de scroll
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const forceScrollToBottom = () => {
    scrollToBottom();
    setTimeout(scrollToBottom, 50);
    setTimeout(scrollToBottom, 100);
    setTimeout(scrollToBottom, 200);
    setTimeout(scrollToBottom, 500);
  };

  // Scroll quand la conversation change
  useEffect(() => {
    if (ai_conversation?.length > 0) {
      forceScrollToBottom();
    }
  }, [ai_conversation]);

  useEffect(() => {
    if (!user?.id) {
      getMe();
    }
    const getOffer = async () => {
      setIsLoadingOffre(true);
      try {
        const response = await getSubscription(type, user?.email);
        setOffer(response.data.offrePurchase);
        setIsLoadingOffre(false);
      } catch (error) {
        setIsLoadingOffre(false);
        console.log(error);
      }
    };
    getOffer();
  }, [user, user?.id, type, getMe]);

  // Ne jamais persister les bulles d'erreur ni les messages d'aiguillage JSON (fuite d'outil)
  const persistConversation = useCallback((msgs: Message[]) => {
    saveConversation(msgs.filter((m) => !m.isError && !isToolRoutingMessage(m.content)));
  }, []);

  // Réhydrater depuis le serveur (source de vérité) — le cache localStorage, déjà chargé par le contexte, sert de paint instantané
  useEffect(() => {
    if (!user?.clientNumber) return;
    (async () => {
      const history = await get_chat_history(user.clientNumber);
      if (history && history.length > 0) {
        const cleaned: Message[] = history
          .filter((msg: any) => !isToolRoutingMessage(msg.content))  // écarter les JSON d'aiguillage d'outil
          .map((msg: any) => ({
            id: generateId(),
            role: msg.role,
            content: msg.content,
            shouldType: false // Les anciens messages ne doivent pas retyper
          }));
        setAi_conversation(cleaned);
        saveConversation(cleaned);
      }
    })();
  }, [user?.clientNumber]);

  const { lastJsonMessage } = useWebSocket(wsUrl, {
    queryParams: { user_id: user?.clientNumber as string },
    onOpen: () => console.log("✅ WebSocket connected"),
    onClose: () => console.log("❌ WebSocket disconnected"),
    onError: (err) => console.error("❌ WebSocket error", err),
    shouldReconnect: () => true,
  });

  const handleUpload = useCallback(async (file: File) => {
    await file_upload(file, ai_thread, selectedTool, user?.clientNumber);
    forceScrollToBottom();
  }, [ai_thread, selectedTool, user?.clientNumber]);

  // Fonction appelée quand le typing d'un message est terminé

  const handleTypingComplete = useCallback((messageId: string) => {
    // L'animation est finie : appliquer les réponses rapides en attente et réafficher les suggestions
    setQuickReplies(pendingQuickReplies.current);
    setSuggestionsReady(true);

    // Mettre à jour le message pour enlever le flag shouldType
    setAi_conversation((prev) => {
      // Vérifier si le message existe et a besoin d'être mis à jour
      const messageToUpdate = prev.find((msg) => msg.id === messageId);
      if (!messageToUpdate || messageToUpdate.shouldType === false) {
        return prev; // Pas de changement, retourner le même tableau
      }

      // Créer le nouveau tableau uniquement avec le message modifié
      const newConversation = prev.map((msg) =>
        msg.id === messageId ? { ...msg, shouldType: false } : msg
      );

      persistConversation(newConversation);

      return newConversation;
    });
  }, [persistConversation]);

  // overrideText : envoi direct (suggestions) sans passer par le state du champ
  const sendMessage = useCallback(async (overrideText?: string) => {
    const userMessage = (overrideText ?? message).trim();
    if (!userMessage || loading) return;

    // Message utilisateur (sans typing)
    const userMsg: Message = {
      id: generateId(),
      role: "user",
      content: userMessage,
      shouldType: false
    };
    setAi_conversation((prev) => {
      const updated = [...prev, userMsg];
      persistConversation(updated);
      return updated;
    });
    setMessage("");
    setLoading(true);
    setSuggestionsReady(false); // masquer jusqu'à la fin de l'animation de la réponse

    setTimeout(forceScrollToBottom, 50);

    try {
      const res: any = await chatCompletionsPremium(userMessage, ai_thread, user?.clientNumber, selectedTool);

      if (!res || res.error || typeof res.message !== "string") {
        // Contrat API : { error: true, message } — afficher une bulle d'erreur distincte, jamais persistée
        const errorMsg: Message = {
          id: generateId(),
          role: "assistant",
          content: res?.message ?? "❌ Désolé, une erreur est survenue. Veuillez réessayer.",
          shouldType: false,
          isError: true
        };
        setAi_conversation((prev) => [...prev, errorMsg]);
        setSuggestionsReady(true); // pas d'animation sur une erreur : réafficher tout de suite
      } else if (isToolRoutingMessage(res.message)) {
        // Réponse = JSON d'aiguillage d'outil (fuite) : ne rien afficher, mais ne pas bloquer les suggestions
        setQuickReplies(Array.isArray(res.quickReplies) ? res.quickReplies : []);
        setSuggestionsReady(true);
        setSelectedTool(null);
      } else {
        // Message assistant AVEC typing
        const assistantMsg: Message = {
          id: generateId(),
          role: "assistant",
          content: res.message,
          shouldType: true  // ← Ce message va avoir l'effet de typing
        };
        setAi_conversation((prev) => {
          const updated = [...prev, assistantMsg];
          persistConversation(updated);
          return updated;
        });
        // Appliquées à la FIN de l'animation de frappe (handleTypingComplete)
        pendingQuickReplies.current = Array.isArray(res.quickReplies) ? res.quickReplies : [];
        setSelectedTool(null); // forçage d'outil one-shot : consommé par cet envoi
      }

      setTimeout(forceScrollToBottom, 100);
    } catch (err) {
      console.error("Erreur de réponse:", err);
      const errorMsg: Message = {
        id: generateId(),
        role: "assistant",
        content: "❌ Désolé, une erreur est survenue. Veuillez réessayer.",
        shouldType: false,
        isError: true
      };
      setAi_conversation((prev) => [...prev, errorMsg]);
      setSuggestionsReady(true);
    } finally {
      setLoading(false);
    }
  }, [message, loading, ai_thread, user?.clientNumber, selectedTool, persistConversation]);

  const handleSuggestedQuestion = useCallback((questionText: string) => {
    // envoi direct, comme sur mobile (le setMessage+setTimeout d'avant capturait un state périmé et n'envoyait jamais)
    sendMessage(questionText);
  }, [sendMessage]);

  // Sélection d'outil : préremplit le champ avec un texte adapté ; la désélection
  // ne vide le champ que si l'utilisateur n'a pas modifié le texte prérempli
  const handleToolSelect = useCallback((tool: string | null) => {
    setShowToolDialog(false);
    if (tool) {
      setMessage(TOOL_PREFILL[tool] ?? "");
    } else if (selectedTool && message === TOOL_PREFILL[selectedTool]) {
      setMessage("");
    }
    setSelectedTool(tool);
  }, [selectedTool, message]);

  useEffect(() => {
    if (!lastJsonMessage) return;
    const { event, data } = lastJsonMessage as { event: string; data: any };
    switch (event) {
      case "file_uploaded":
        const fileMsg: Message = {
          id: generateId(),
          role: "assistant",
          content: data.assistantReply,
          shouldType: true  // ← Le message du WebSocket peut aussi typer
        };
        setAi_conversation((prev) => {
          const updated = [...prev, fileMsg];
          persistConversation(updated);
          return updated;
        });
        forceScrollToBottom();
        break;
      case "processing_done":
        console.log("✅ Processing finished:", data.fileName);
        break;
      default:
        console.log("⚠️ Unknown event:", event, data);
    }
  }, [lastJsonMessage]);

  // if (isLoadingOffre) return <Loader />;

  if ((!offer && user?.type === "individual" && !isLoadingOffre)) {
    return (
      <UnauthorizedPage
        message="Accès à la fonctionnalité premium"
        alertText="Cette fonctionnalité est réservée à nos abonnés."
        btnText="Découvrir nos offres"
        urlBtn="id-tracker-pricing"
      />
    );
  }

  return (
    <>
      <div className="chat-container-modern">
        <div className="chat-header">
          <div className="header-info">
            <IconBack
              className="PageHeader__icon mr-5 pointer"
              fill={"#171C60"}
              width={20}
              onClick={() => navigate(-1)}
            />
            <div className="ai-avatar">
              <img src={imgKuma} alt="" />
              <span className="online-dot" />
            </div>
            <div className="ai-status">
              <h2>Kuma</h2>
              <span className="status">Assistant IA</span>
            </div>
          </div>
        </div>

        <div className="messages-area" ref={messagesAreaRef}>
          {ai_conversation?.length === 0 && (
            <div className="welcome-message-modern">
              <div className="greeting">
                Bonjour {user?.firstname || "cher utilisateur"} 👋
              </div>
              <div className="subtitle">
                Ici Kuma, l'agent IA d'ID Protect. Comment puis-je vous aider aujourd'hui ?
              </div>
            </div>
          )}

          <AnimatePresence>
            {ai_conversation
              .filter((msg: Message) => !isToolRoutingMessage(msg.content))  // ne pas afficher les JSON d'aiguillage d'outil
              .map((msg: Message) => (
              <MessageBubble
                key={msg.id}
                id={msg.id}
                role={msg.role}
                content={msg.content}
                toolLinks={toolLinksResolved}
                shouldType={msg.shouldType || false}
                typingSpeed={15}
                onTypingComplete={handleTypingComplete}
                forceScrollToBottom={scrollToBottom}
                isError={msg.isError || false}
              />
            ))}
          </AnimatePresence>

          {loading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="message-bubble assistant"
            >
              <div className="avatar">
                <div className="avatar-icon">
                  <img src={imgKuma} alt="" />
                </div>
              </div>
              <div className="message-content">
                <div className="bubble">
                  <div className="typing-indicator-modern">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {!selectedTool && suggestionsReady && (
          <SuggestedQuestions
            questions={
              quickReplies.length > 0
                ? quickReplies.map((text, i) => ({ id: `qr-${i}-${text.slice(0, 12)}`, text }))
                : SUGGESTED_QUESTIONS
            }
            onSelect={handleSuggestedQuestion}
          />
        )}

        <div className="input-section">
          {selectedTool && (
            <div className="selected-tool-chip">
              <IconTool width={13} height={13} className="chip-icon" />
              {TOOL_LABELS[selectedTool] ?? selectedTool}
              <button
                type="button"
                onClick={() => handleToolSelect(null)}
                title="Retirer l'outil"
              >
                ×
              </button>
            </div>
          )}
          <ChatInput
            value={message}
            onChange={setMessage}
            onSend={sendMessage}
            onFileUpload={handleUpload}
            onToolSelect={() => setShowToolDialog(true)}
            isTyping={false}
            disabled={loading}
          />
        </div>

        {showToolDialog && (
          <ToolSelectDialog
            selected={selectedTool}
            onSelect={handleToolSelect}
            onClose={() => setShowToolDialog(false)}
          />
        )}

      </div>
    </>
  );
};

export default TestAI;